import {axisBottom, axisLeft} from 'd3-axis'
import {controller, target} from '@github/catalyst'
import {curveBasis, area as d3area} from 'd3-shape'
import {max as d3max, descending} from 'd3-array'
import {scaleLinear, scaleTime} from 'd3-scale'
import {timeFormat, utcFormat} from 'd3-time-format'
import type {Selection} from 'd3-selection'
import {TemplateInstance} from '@github/template-parts'
import {brushX} from 'd3-brush'
import {entries} from 'd3-collection'
import {fetchPoll} from '@github-ui/fetch-utils'
import formatSymbol from '../d3/format-symbol'
import memoize from '@github/memoize'
import {months} from '../locale'
// eslint-disable-next-line no-restricted-imports
import {on} from 'delegated-events'
import {pushState} from '@github-ui/browser-history-state'
import {select} from 'd3-selection'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GraphNetworkData = any

const cachedData = memoize(fetchData)
async function fetchData(url: string): Promise<GraphNetworkData> {
  const response = await fetchPoll(url, {headers: {accept: 'application/json'}})
  return await response.json()
}

@controller
class ContributorsGraphElement extends HTMLElement {
  @target graph: HTMLElement

  async connectedCallback() {
    const graph = this.graph
    const url = graph.getAttribute('data-url')
    if (!url) {
      return
    }

    // Remove possibly existing cached graph.
    /* eslint-disable-next-line custom-elements/no-dom-traversal-in-connectedcallback */
    for (const el of graph.querySelectorAll('svg.viz')) {
      el.remove()
    }

    graph.classList.add('is-graph-loading')
    graph.classList.remove('is-graph-load-error', 'is-graph-empty')

    try {
      const data = await cachedData(url)
      graph.classList.remove('is-graph-loading')
      if (data != null && data.unusable) {
        graph.classList.add('is-graph-without-usable-data')
      } else if (
        (data != null && data.length === 0) ||
        (data != null && data.summary != null && data.summary.total === 0) ||
        (data[0] != null && data[0].weeks != null && data[0].weeks.length === 0)
      ) {
        graph.classList.add('is-graph-empty')
      } else {
        render(graph, data)
      }
    } catch (error) {
      graph.classList.remove('is-graph-loading')
      graph.classList.add('is-graph-load-error')
      throw error
    }
  }
}

type DateRange = [Date, Date]

interface Author {
  id: number
  login: string
  avatar: string
  path: string
  hovercard_url: string
}

interface Week {
  c: number
  a: number
  d: number
  w: number
  date: Date
}

interface Contributor {
  author: Author
  total: number
  weeks: Week[]
  c: number
  a: number
  d: number
}

type Data = Contributor[]

function formatUTCDate(date: Date) {
  const month = months[date.getUTCMonth()]
  return month ? `${month.slice(0, 3)} ${date.getUTCDate()}, ${date.getUTCFullYear()}` : ''
}

// Updates HTML page header with current date range
function updatePageHeader(dateRange: DateRange) {
  const start = formatUTCDate(dateRange[0])
  const end = formatUTCDate(dateRange[1])
  const element = document.querySelector<HTMLElement>('.js-date-range')
  if (element) {
    element.textContent = `${start} – ${end}`
  }
}

// If we only have 1 week of data, we need to pad the data with a previous week
// so we can have two points to draw a proper segment
function padWeekData(data: Data) {
  const date = data[0]!.weeks[0]!.date
  const start = new Date(date.getTime() - 7 * 24 * 60 * 60 * 1000)
  for (const author of data) {
    author.weeks.unshift({
      a: 0,
      c: 0,
      d: 0,
      date: start,
      w: start.getTime() / 1000,
    })
  }
}

const formatDate = utcFormat('%Y-%m-%d')

function parseDate(d: number): Date {
  return new Date(~~d * 1000)
}

function filterWeeksWithRange(data: Data, range: DateRange): Data {
  const [start, end] = range
  return data.map(function (contributor) {
    const cloned = Object.assign({}, contributor)
    cloned.weeks = cloned.weeks.filter(w => w.date >= start && w.date <= end)
    return cloned
  })
}

function summarizeTotalsForWeeks(data: Data, dateRange: DateRange, contributionType: ContributionType): Data {
  const filteredData = filterWeeksWithRange(data, dateRange)
  for (const d of filteredData) {
    let cmts = 0
    let adds = 0
    let dels = 0
    for (const week of d.weeks) {
      cmts += week.c
      adds += week.a
      dels += week.d
    }
    d.c = cmts
    d.a = adds
    d.d = dels
  }
  return filteredData.sort((a, b) => descending(a[contributionType], b[contributionType]))
}

function parseDates(data: Data) {
  for (const author of data) {
    for (const week of author.weeks) {
      week.date = new Date(week.w * 1000)
    }
  }
}

interface Total {
  key: string
  value: {c: number; a: number; d: number}
}

function calculateOverallTotals(data: Data): Total[] {
  const totals: {[key: number]: {c: number; a: number; d: number}} = {}
  for (const author of data) {
    for (const week of author.weeks) {
      let total = totals[week.w]
      if (!total) {
        total = totals[week.w] = {c: 0, a: 0, d: 0}
      }
      total.c += week.c
      total.a += week.a
      total.d += week.d
    }
  }
  return entries(totals)
}

function updateURL(dateRange: DateRange, contributionType: string) {
  const url = new URL(window.location.href, window.location.origin)
  const params = new URLSearchParams(url.search)
  params.set('from', formatDate(dateRange[0]))
  params.set('to', formatDate(dateRange[1]))
  params.set('type', contributionType)
  url.search = params.toString()

  pushState(null, '', url.toString())
}

function currentContributionType(): string {
  const params = new URLSearchParams(window.location.search)
  return params.get('type') || 'c'
}

function currentDateRange(): DateRange | undefined {
  const params = new URLSearchParams(window.location.search)
  const start = params.get('from')
  const end = params.get('to')
  if (start && end) {
    return [new Date(start), new Date(end)]
  }
  return
}

interface State {
  data: Data
  totals: Total[]
  fullTimeSpan: DateRange
  selectedTimeSpan: DateRange
}

// Additions, deletions and commits
type ContributionType = 'a' | 'd' | 'c'

const states = new WeakMap()

function createRange(start: Date, end: Date): DateRange {
  const now = new Date()
  if (end > now) {
    end = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
  }
  return [start, end]
}

function render(container: HTMLElement, data: Data) {
  parseDates(data)

  if (data[0]!.weeks.length === 1) {
    padWeekData(data)
  }

  const totals = calculateOverallTotals(data)
  const fullTimeSpan = createRange(
    parseDate(Number(totals[0]!.key)),
    parseDate(~~totals[totals.length - 1]!.key + 6 * 24 * 3600),
  )
  const dateRangeFromUrl = currentDateRange()
  const selectedTimeSpan = dateRangeFromUrl || fullTimeSpan

  const state = {data, totals, fullTimeSpan, selectedTimeSpan}
  states.set(container, state)

  updatePageHeader(selectedTimeSpan)
  renderMasterChart(container, state)
  renderContributorsList(container, state, dateRangeFromUrl)
}

function dateRangeSelected(container: Element, selectedRange?: DateRange) {
  const state = states.get(container)!
  if (!selectedRange || formatDate(selectedRange[0]) === formatDate(selectedRange[1])) {
    state.selectedTimeSpan = state.fullTimeSpan
  } else {
    state.selectedTimeSpan = selectedRange
  }
  updateURL(state.selectedTimeSpan, currentContributionType())
  updatePageHeader(state.selectedTimeSpan)
  renderContributorsList(container, state, selectedRange)
}

on(
  'details-menu-selected',
  '.js-contribution-menu',
  function (event) {
    const button = event.detail.relatedTarget as HTMLButtonElement
    const container = document.querySelector<HTMLElement>('.js-graph')!
    const state = states.get(container)!
    if (state && currentContributionType() !== button.value) {
      updateURL(state.selectedTimeSpan, button.value)
      renderMasterChart(container, state)
      renderContributorsList(container, state)
    }
  },
  {capture: true},
)

function renderContributorsList(container: Element, state: State, selectedRange?: DateRange) {
  const {selectedTimeSpan} = state
  const [startDate, endDate] = selectedTimeSpan

  // Remove old wrapper
  const oldRoot = document.querySelector('#contributors ol')
  if (oldRoot) {
    oldRoot.remove()
  }

  // Get data
  const params = new URLSearchParams(window.location.search)
  const contributionType = (params.get('type') || 'c') as ContributionType
  const data = summarizeTotalsForWeeks(state.data, selectedTimeSpan, contributionType)

  // Set up wrapper and bounds
  const root = document.createElement('ol')
  const vis = select(root).attr('class', 'contrib-data list-style-none')

  // Remove each previous graph
  for (const person of document.querySelectorAll('.contrib-person')) {
    person.remove()
  }

  // Accessors
  const authorHoverCardUrlAccessor = (d: Contributor) => d.author.hovercard_url
  const authorPathAccessor = (d: Contributor) => d.author.path
  const authorAvatarUrlAccessor = (d: Contributor) => d.author.avatar
  const authorLoginAccessor = (d: Contributor) => d.author.login

  const graphElement = document.querySelector<HTMLElement>('.graphs')
  let repoURL = ''

  if (graphElement) {
    repoURL = graphElement.getAttribute('data-repo-url') || ''
  }

  const template = document.querySelector<HTMLTemplateElement>('#graphs-contributors-person-item')!

  // Create a graph for each person
  const personSelection = vis
    .selectAll('.contrib-person')
    .data(data.filter((d: Contributor) => d[contributionType]))
    .enter()
    .append((d, i) => {
      const contributorParams = new URLSearchParams()
      contributorParams.set('author', authorLoginAccessor(d))

      if (selectedRange) {
        contributorParams.set('since', formatDate(selectedRange[0]))
        contributorParams.set('until', formatDate(selectedRange[1]))
      }

      const instance = new TemplateInstance(template, {
        hoverCardUrl: authorHoverCardUrlAccessor(d),
        authorUrl: authorPathAccessor(d),
        authorAvatarUrl: authorAvatarUrlAccessor(d),
        authorLogin: authorLoginAccessor(d),
        place: `#${i + 1}`,
        contributorUrl: `${repoURL}/commits?${contributorParams.toString()}`,
        contributorUrlText: `${new Intl.NumberFormat('en-US').format(d.c)} ${d.c === 1 ? 'commit' : 'commits'}`,
        linesAdded: `${new Intl.NumberFormat('en-US').format(d.a)} ++`,
        linesDeleted: `${new Intl.NumberFormat('en-US').format(d.d)} --`,
      })

      const li = instance.children[0]!
      if ((i + 1) % 2) {
        li.classList.add('pr-2')
      } else {
        li.classList.add('pl-2')
      }

      return li
    })

  renderContributorItem(
    personSelection,
    data,
    parseInt(container.getAttribute('data-graph-width')!, 10),
    startDate,
    endDate,
  )

  container.appendChild(root)
}

function renderContributorItem(
  container: Selection<Element, Contributor, HTMLOListElement, unknown>,
  data: Contributor[],
  width: number,
  startDate: Date,
  endDate: Date,
) {
  // Fetch the type state from the URL
  const params = new URLSearchParams(window.location.search)
  const contributionType = (params.get('type') || 'c') as ContributionType

  // Set up accessors
  const dateAccessor = (d: Week) => d.date
  const typeAccessor = (d: Week) => d[contributionType]

  // Set up dimensions of graph
  const dimensions = {
    width,
    height: 140,
    margin: {
      top: 10,
      left: 20,
      right: 20,
      bottom: 20,
    },
    boundedHeight: 0,
    boundedWidth: 0,
  }

  dimensions.boundedHeight = dimensions.height - dimensions.margin.bottom - dimensions.margin.top
  dimensions.boundedWidth = dimensions.width - dimensions.margin.left - dimensions.margin.right

  // Set up scales
  const max = d3max(data, d => d3max(d.weeks, typeAccessor))!
  const xScale = scaleTime().domain([startDate, endDate]).range([0, dimensions.width])
  const yScale = scaleLinear().domain([0, max]).range([dimensions.boundedHeight, 0])

  // Create area
  const area = d3area<Week>()
    .curve(curveBasis)
    .x(d => xScale(dateAccessor(d)))
    .y0(() => dimensions.boundedHeight)
    .y1(d => yScale(typeAccessor(d)))

  // Set up axis generators
  const yAxisGenerator = axisLeft<number>(yScale)
    .ticks(2)
    .tickSize(-dimensions.width)
    .tickPadding(10)
    .tickFormat(formatSymbol)
  const xAxisGenerator = axisBottom<Date>(xScale)
  if (data[0]!.weeks.length < 5) {
    xAxisGenerator.ticks(data[0]!.weeks.length).tickFormat(timeFormat('%x'))
  }

  // Create a viewbox to hold the graph
  const svg = container
    .select('span.Box')
    .append('svg')
    .attr(
      'viewBox',
      `0 0 ${dimensions.width + (dimensions.margin.left + dimensions.margin.right)} ${
        dimensions.height + dimensions.margin.top + dimensions.margin.bottom
      }`,
    )
    .attr('class', 'capped-card-content')
    .attr('preserveAspectRatio', 'xMinYMin meet')
    .append('g')
    .attr('transform', `translate(${dimensions.margin.left},${dimensions.margin.top})`)

  // Render x axis
  const xAxis = svg
    .append('g')
    .attr('class', 'x axis')
    // @ts-expect-error types should be validated here
    .classed('dense', xAxisGenerator.ticks()[0] >= 10)
    .attr('transform', `translate(0, ${dimensions.boundedHeight})`)
    .call(xAxisGenerator)

  // Customize x axis
  xAxis
    .selectAll('.tick text')
    .attr('fill', '#6a737d')
    .style('display', (_, i) => (i % 3 !== 0 ? 'none' : 'block'))
  svg.select('.x.dense text').attr('dx', 7)

  // Render y axis
  const yAxis = svg.append('g').attr('class', 'y axis').call(yAxisGenerator)

  // Customize y axis
  yAxis
    .selectAll('.y.axis g text')
    .attr('fill', '#6a737d')
    .attr('dx', dimensions.width / 2)
    .style('display', (_, i) => (i === 0 ? 'none' : 'block'))
    .classed('midlabel', true)

  // Draw area
  svg.append('path').attr('d', d => area(d.weeks))
}

function renderMasterChart(container: Element, state: State) {
  const {totals, fullTimeSpan, selectedTimeSpan} = state
  const [startDate, endDate] = fullTimeSpan

  const dimensions = {
    width: container.clientWidth,
    height: 160,
    margin: {
      top: 20,
      left: 50,
      right: 20,
      bottom: 50,
    },
    boundedWidth: 0,
    boundedHeight: 0,
  }
  dimensions.boundedWidth = dimensions.width - dimensions.margin.left - dimensions.margin.right
  dimensions.boundedHeight = dimensions.height - dimensions.margin.top - dimensions.margin.bottom

  const params = new URLSearchParams(window.location.search)
  const contributionType = (params.get('type') || 'c') as ContributionType

  // Set up accessors
  const dateAccessor = (d: Total) => parseDate(parseInt(d.key, 10))
  const typeAccessor = (d: Total) => d.value[contributionType]

  // Set up scales
  const xScale = scaleTime().domain([startDate, endDate]).range([0, dimensions.boundedWidth])
  const yScale = scaleLinear()
    .domain([0, d3max(totals, typeAccessor)!])
    .range([dimensions.height, 0])

  // Create area
  const area = d3area<Total>()
    .curve(curveBasis)
    .x(d => xScale(dateAccessor(d)))
    .y0(() => dimensions.height)
    .y1(d => yScale(typeAccessor(d)))

  // Why remove master svg?
  select('#contributors-master svg').remove()

  // Set up a "viewBox". Not sure why this is needed.
  const bounds = select('#contributors-master')
    .data([totals])
    .append('svg')
    .attr('viewBox', `0 0 ${dimensions.width} ${dimensions.height + dimensions.margin.top + dimensions.margin.bottom}`)
    .attr('preserveAspectRatio', 'xMinYMin meet')
    .attr('class', 'viz')
    .attr('data-hpc', '')
    .append('g')
    .attr('transform', `translate(${dimensions.margin.left},${dimensions.margin.top})`)

  // Set up axis
  const yAxisGenerator = axisLeft(yScale)
    .ticks(4)
    .tickSize(-dimensions.width + dimensions.margin.left + dimensions.margin.right)
    .tickPadding(10)
    // @ts-expect-error types should be validated here
    .tickFormat(formatSymbol)

  const xAxisGenerator = axisBottom(xScale)
  if (totals.length < 5) {
    xAxisGenerator.ticks(totals.length)
  }

  // Draw x axis
  const xAxis = bounds
    .append('g')
    .attr('class', 'x axis')
    .attr('transform', `translate(0, ${dimensions.height})`)
    .call(xAxisGenerator)

  // Change tick color. TODO: Do this in a graph utility class?
  xAxis.selectAll('.tick text').attr('fill', '#6a737d')

  // Draw y axis and change tick color
  const yAxis = bounds.append('g').attr('class', 'y axis').call(yAxisGenerator)

  // Change tick color. TODO: Do this in a graph utility class?
  yAxis.selectAll('.tick text').attr('fill', '#6a737d')

  // Draw the area
  bounds.append('g').append('path').attr('class', 'area').attr('d', area)

  // Selection
  function selectionStart() {
    bounds.classed('selecting', true)
  }
  function selectionEnd(event: {selection: number[]}) {
    const isEmpty = new Boolean(event.selection && event.selection.length)
    const selection = event.selection || []
    bounds.classed('selecting', !isEmpty)
    const dates = selection.map(xScale.invert)

    let range: DateRange | undefined = undefined
    if (dates.length === 2) {
      const [start, end] = dates
      if (!(start instanceof Date)) {
        throw new Error('invariant')
      }
      if (!(end instanceof Date)) {
        throw new Error('invariant')
      }
      range = [start, end]
    }

    dateRangeSelected(container, range)
  }

  // Brush
  const brush = brushX<Total[]>()
    .on('start', selectionStart)
    .on('end', selectionEnd)
    .extent([
      [0, 0],
      [dimensions.width - (dimensions.margin.left + dimensions.margin.right), dimensions.height],
    ])

  const gBrush = bounds.append('g').attr('class', 'selection').call(brush)

  if (
    formatDate(selectedTimeSpan[0]) !== formatDate(startDate) ||
    formatDate(selectedTimeSpan[1]) !== formatDate(endDate)
  ) {
    gBrush.call(brush.move, selectedTimeSpan.map(xScale))
  }
}
